"use client"

import { useEffect, useRef, useState, useTransition } from "react"
import { useParams, useRouter } from "next/navigation"

import {
  ActiveVehicle,
  FitmentMake,
  FitmentModel,
  GarageVehicle,
  ResolvedVehicle,
  addGarageVehicle,
  listEngines,
  listMakes,
  listModels,
  listYears,
  resolveVehicle,
  setActiveVehicle,
} from "@lib/data/fitment"
import NativeSelect from "@modules/common/components/native-select"
import { Button, Text, clx } from "@modules/common/components/ui"
import EngineSelect from "@modules/ymm/components/engine-select"

type VehicleSelectorProps = {
  garageVehicles?: GarageVehicle[]
  isLoggedIn?: boolean
  // When provided, the selector hands the resolved vehicle to the caller
  // instead of setting the active-vehicle cookie and navigating. Only
  // passable from client components (e.g. the garage page).
  onResolved?: (vehicle: ActiveVehicle) => void | Promise<void>
  className?: string
}

const vehicleLabel = (v: { year: number; make: string; model: string }) =>
  `${v.year} ${v.make} ${v.model}`

const VehicleSelector = ({
  garageVehicles = [],
  isLoggedIn = false,
  onResolved,
  className,
}: VehicleSelectorProps) => {
  const router = useRouter()
  const { countryCode } = useParams() as { countryCode: string }
  const [isPending, startTransition] = useTransition()

  const [years, setYears] = useState<number[]>([])
  const [makes, setMakes] = useState<FitmentMake[]>([])
  const [models, setModels] = useState<FitmentModel[]>([])

  const [year, setYear] = useState("")
  const [makeId, setMakeId] = useState("")
  const [modelId, setModelId] = useState("")

  // Resolved eagerly when the model is picked so the optional engine step can
  // show up before the shopper hits Go. Skipped silently when the backend has
  // no engine data for the vehicle.
  const [resolved, setResolved] = useState<ResolvedVehicle | null>(null)
  const [engines, setEngines] = useState<string[]>([])
  const [engine, setEngine] = useState("")
  const resolveRequestRef = useRef(0)

  const [isResolving, setIsResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savePrompt, setSavePrompt] = useState<ActiveVehicle | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    listYears().then(setYears)
  }, [])

  const goToResults = () => {
    startTransition(() => {
      router.push(`/${countryCode}/vehicle`)
      router.refresh()
    })
  }

  const clearResolved = () => {
    resolveRequestRef.current += 1
    setResolved(null)
    setEngines([])
    setEngine("")
  }

  const handleYearChange = (value: string) => {
    setYear(value)
    setMakeId("")
    setModelId("")
    setMakes([])
    setModels([])
    setError(null)
    clearResolved()

    if (value) {
      listMakes(Number(value)).then(setMakes)
    }
  }

  const handleMakeChange = (value: string) => {
    setMakeId(value)
    setModelId("")
    setModels([])
    setError(null)
    clearResolved()

    if (value) {
      listModels(Number(year), Number(value)).then(setModels)
    }
  }

  const handleModelChange = (value: string) => {
    setModelId(value)
    setError(null)
    clearResolved()

    if (!value) {
      return
    }

    const requestId = resolveRequestRef.current

    resolveVehicle(Number(year), Number(makeId), Number(value)).then(
      async (result) => {
        if (requestId !== resolveRequestRef.current || !result) {
          return
        }

        setResolved(result)

        const engineList = await listEngines(result.base_vehicle_id)

        if (requestId === resolveRequestRef.current) {
          setEngines(engineList)
        }
      }
    )
  }

  const isInGarage = (baseVehicleId: number) =>
    garageVehicles.some((g) => g.base_vehicle_id === baseVehicleId)

  const handleGo = async () => {
    if (!year || !makeId || !modelId) {
      return
    }

    setIsResolving(true)
    setError(null)

    try {
      const resolvedVehicle =
        resolved ??
        (await resolveVehicle(Number(year), Number(makeId), Number(modelId)))

      if (!resolvedVehicle) {
        setError("We couldn't find that vehicle. Try another selection.")
        return
      }

      const active: ActiveVehicle = {
        base_vehicle_id: resolvedVehicle.base_vehicle_id,
        ...resolvedVehicle.vehicle,
        engine: engine || null,
      }

      if (onResolved) {
        await onResolved(active)
        return
      }

      await setActiveVehicle(active)

      if (isLoggedIn && !isInGarage(active.base_vehicle_id)) {
        setSavePrompt(active)
        return
      }

      goToResults()
    } finally {
      setIsResolving(false)
    }
  }

  const handleGarageChip = async (garageVehicle: GarageVehicle) => {
    if (onResolved) {
      await onResolved({
        base_vehicle_id: garageVehicle.base_vehicle_id,
        ...garageVehicle.vehicle,
        engine: null,
      })
      return
    }

    await setActiveVehicle({
      base_vehicle_id: garageVehicle.base_vehicle_id,
      ...garageVehicle.vehicle,
      engine: null,
    })
    goToResults()
  }

  const handleSaveToGarage = async () => {
    if (!savePrompt) {
      return
    }

    setIsSaving(true)

    try {
      await addGarageVehicle(savePrompt.base_vehicle_id)
    } finally {
      setIsSaving(false)
      goToResults()
    }
  }

  if (savePrompt) {
    return (
      <div
        className={clx(
          "flex flex-col gap-y-3 p-4 border border-ui-border-base rounded-md bg-white",
          className
        )}
        data-testid="save-to-garage-prompt"
      >
        <Text className="txt-medium text-ui-fg-base">
          Save your {vehicleLabel(savePrompt)} to your garage?
        </Text>
        <div className="flex gap-x-2">
          <Button
            size="small"
            onClick={handleSaveToGarage}
            isLoading={isSaving}
            data-testid="save-to-garage-button"
          >
            Save to garage
          </Button>
          <Button
            size="small"
            variant="secondary"
            onClick={goToResults}
            disabled={isSaving}
            data-testid="skip-save-to-garage-button"
          >
            Not now
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={clx("flex flex-col gap-y-4", className)}>
      {garageVehicles.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <Text className="txt-compact-small-plus text-ui-fg-subtle">
            Your Garage
          </Text>
          <div className="flex flex-wrap gap-2" data-testid="garage-chips">
            {garageVehicles.map((garageVehicle) => (
              <button
                key={garageVehicle.id}
                type="button"
                onClick={() => handleGarageChip(garageVehicle)}
                className="inline-flex items-center gap-x-1 rounded-full border border-ui-border-base bg-ui-bg-subtle px-3 py-1 text-sm text-ui-fg-subtle hover:text-ui-fg-base hover:bg-ui-bg-field-hover transition-colors"
                data-testid="garage-chip"
              >
                {garageVehicle.is_default && <span aria-hidden>★</span>}
                {garageVehicle.nickname || vehicleLabel(garageVehicle.vehicle)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 small:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        <NativeSelect
          placeholder="Year"
          value={year}
          onChange={(e) => handleYearChange(e.target.value)}
          data-testid="ymm-year-select"
          aria-label="Year"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          placeholder="Make"
          value={makeId}
          onChange={(e) => handleMakeChange(e.target.value)}
          disabled={!year}
          data-testid="ymm-make-select"
          aria-label="Make"
        >
          {makes.map((make) => (
            <option key={make.make_id} value={make.make_id}>
              {make.name}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          placeholder="Model"
          value={modelId}
          onChange={(e) => handleModelChange(e.target.value)}
          disabled={!makeId}
          data-testid="ymm-model-select"
          aria-label="Model"
        >
          {models.map((model) => (
            <option key={model.model_id} value={model.model_id}>
              {model.name}
            </option>
          ))}
        </NativeSelect>
        <Button
          onClick={handleGo}
          disabled={!year || !makeId || !modelId}
          isLoading={isResolving || isPending}
          data-testid="ymm-go-button"
        >
          Go
        </Button>
      </div>

      {engines.length > 0 && (
        <div
          className="flex flex-col gap-y-1"
          data-testid="ymm-engine-step"
        >
          <Text className="txt-compact-small-plus text-ui-fg-subtle">
            Engine (optional)
          </Text>
          <EngineSelect
            engines={engines}
            value={engine}
            onChange={setEngine}
            className="small:max-w-md"
            data-testid="ymm-engine-select"
          />
        </div>
      )}

      {error && (
        <Text role="alert" className="text-sm text-rose-500" data-testid="ymm-error">
          {error}
        </Text>
      )}
    </div>
  )
}

export default VehicleSelector
